import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Shield, Globe, ArrowRight } from "lucide-react";
import { supabase } from "../supabaseClient";

import Logo from "../components/Logo";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryError = searchParams.get("error");
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLogin, setIsLogin] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [authError, setAuthError] = useState("");
  
  useEffect(() => {
    if (!loading && user) {
      if (user.role === "admin") navigate("/admin", { replace: true });
      else if (user.role === "worker") navigate("/worker", { replace: true });
      else navigate("/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  const handleAuth = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setIsSending(true);
    setAuthError("");

    try {
      if (isLogin) {
        // EXACTLY ONE call on button click
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
           if (error.message.includes("credential") || error.message.includes("json") || error.message.includes("stream")) {
             setAuthError("No account found, or incorrect password.");
           } else {
             setAuthError(error.message);
           }
        }
        // If success, AuthContext auto-detects via onAuthStateChange and redirects via useEffect
      } else {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { full_name: email.split("@")[0] } }
        });
        
        if (error) {
           if (error.message.includes("already")) {
             setAuthError("This email already has an account. Please click 'Sign in' below instead.");
           } else {
             setAuthError(error.message);
           }
        } else if (data?.user && !data?.session) {
          setAuthError("Please check your email to confirm your new account.");
        }
      }
    } catch (err) {
      setAuthError("Something went wrong processing your request.");
    }
    
    setIsSending(false);
  };

  const handleResetPassword = async () => {
    if (!email) {
      setAuthError("Enter your email address above first, then click Forgot Password.");
      return;
    }
    setIsSending(true);
    setAuthError("");
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + "/login",
      });
      if (error) {
        setAuthError(error.message);
      } else {
        setResetSent(true);
      }
    } catch (err) {
      setAuthError("Failed to send reset email. Please try again.");
    }
    setIsSending(false);
  };

  if (loading) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] to-[#e8f4fd] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="glass-card rounded-3xl p-10">
          <div className="text-center mb-8">
            <Logo className="justify-center mb-5 scale-125" />
            <h1 className="text-2xl font-bold font-outfit tracking-tight text-gradient mt-4">
              {isLogin ? "Sign In to Your Account" : "Create an Account"}
            </h1>
            <p className="text-[#6B7280] text-sm mt-2">
              Track your medical journey and manage all services in one place
            </p>
          </div>

          {(queryError || authError) && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-red-600 text-sm text-center">
              {authError || "Authentication failed. Please try again."}
            </div>
          )}

          <form onSubmit={handleAuth} className="space-y-4">
            <div>
              <input
                type="email"
                required
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-[#0B3C5D] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3FA7D6] focus:border-transparent transition-all mb-4"
              />
              <input
                type="password"
                required
                placeholder="Password (minimum 6 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-[#0B3C5D] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3FA7D6] focus:border-transparent transition-all"
              />
            </div>
            <button
              type="submit"
              disabled={isSending || !email || password.length < 6}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-[#0B3C5D] hover:bg-[#1a4a6b] text-white rounded-xl font-semibold text-base transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed shadow-[0_4px_20px_rgba(11,60,93,0.15)] group"
            >
              {isSending ? "Processing..." : (isLogin ? "Sign In" : "Sign Up")}
              {!isSending && <ArrowRight className="w-4 h-4 ml-1 opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />}
            </button>
          </form>

          <p className="text-center text-sm text-[#6B7280] mt-6">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button onClick={() => { setIsLogin(!isLogin); setAuthError(""); setResetSent(false); }} className="text-[#3FA7D6] font-semibold hover:underline">
              {isLogin ? "Sign up" : "Sign in"}
            </button>
          </p>

          {isLogin && (
            <div className="text-center mt-3">
              {resetSent ? (
                <p className="text-sm text-green-600 font-medium">✓ Password reset email sent! Check your inbox.</p>
              ) : (
                <button onClick={handleResetPassword} disabled={isSending} className="text-sm text-[#6B7280] hover:text-[#3FA7D6] transition-colors underline">
                  Forgot password? Send reset link
                </button>
              )}
            </div>
          )}
        </div>

        <p className="text-center text-sm text-[#6B7280] mt-6">
          <a href="/" className="hover:text-[#3FA7D6] transition-colors flex items-center justify-center gap-1">
            <Globe className="w-3.5 h-3.5" /> Back to Monimek.com
          </a>
        </p>
      </div>
    </div>
  );
}
